'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/slw/shared/lib/utils';

const checkboxVariants = cva(
  `peer shrink-0 rounded-sm border shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring 
  disabled:cursor-not-allowed disabled:opacity-50 
  transition-all duration-200 ease-in-out
  hover:border-default`,
  {
    variants: {
      color: {
        grey: [
          'border-gray-input',
          'bg-gray-input',
          'data-[state=checked]:border-primary',
          'hover:border-primary',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-gray-input',
        ],
        dark: [
          'border-dark-grey-input',
          'hover:border-primary',
          'bg-dark-grey-input',
          'data-[state=checked]:border-primary',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-dark-grey-input',
        ],
        white: [
          'border-secondary',
          'bg-secondary',
          'hover:border-primary',
          'data-[state=checked]:border-primary',
          'data-[state=checked]:bg-primary',
          'data-[state=checked]:text-secondary',
        ],
        green: [
          'hover:border-success',
          'bg-success',
          'text-success',
          'hover:border-primary',
          'data-[state=checked]:border-success',
          'data-[state=checked]:bg-success',
          'data-[state=checked]:text-white',
        ],
      },
      size: {
        sm: 'h-4 w-4 [&_svg]:h-3 [&_svg]:w-3',
        md: 'h-5 w-5 [&_svg]:h-4 [&_svg]:w-4',
        lg: 'h-6 w-6 [&_svg]:h-5 [&_svg]:w-5',
      },
      variant: {
        default: '',
        outline:
          'data-[state=checked]:bg-transparent data-[state=checked]:text-primary data-[state=checked]:border-primary ',
      },
    },
    defaultVariants: {
      color: 'white',
      size: 'md',
      variant: 'default',
    },
  },
);

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    color?: VariantProps<typeof checkboxVariants>['color'];
    size?: VariantProps<typeof checkboxVariants>['size'];
    variant?: VariantProps<typeof checkboxVariants>['variant'];
  }
>(({ className, color = 'white', size = 'md', variant = 'default', ...props }, ref) => (
  <CheckboxPrimitive.Root ref={ref} className={cn(checkboxVariants({ color, size, variant }), className)} {...props}>
    <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
      <Check />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
